/**
 * Utility to resolve which external success URL to redirect to based on form data and language.
 */

import {
  evaluateAllConditions,
  evaluateAnyConditionGroup,
  evaluateSingleCondition
} from './evaluateCondition'
import type { SuccessRedirectRule } from './types'

/**
 * Resolves a redirect URL that may be a string or language-specific record.
 * @param redirectUrl - Either a string URL or a Record mapping language codes to URLs
 * @param language - Current language code (e.g., 'it', 'en', 'fr', 'es')
 * @returns The resolved URL string
 */
function resolveRedirectUrl(
  redirectUrl: string | Record<string, string>,
  language: string
): string {
  // If it's a simple string, return as-is
  if (typeof redirectUrl === 'string') {
    return redirectUrl
  }

  // If it's a language-specific object, try to find the language
  if (typeof redirectUrl === 'object' && redirectUrl !== null) {
    // Try exact language match first
    if (redirectUrl[language]) {
      return redirectUrl[language]
    }

    // Try 2-letter language code (in case full locale was passed, e.g., 'it-IT' → 'it')
    const langPrefix = language.split('-')[0]
    if (redirectUrl[langPrefix]) {
      return redirectUrl[langPrefix]
    }

    // Fallback to first available language in the object
    const urls = Object.values(redirectUrl)
    if (urls.length > 0) {
      return urls[0]
    }
  }

  // Safeguard: return empty string if nothing worked
  return ''
}

/**
 * Evaluates a single SuccessRedirectRule against the provided form data.
 * Returns true if all conditions in the rule are satisfied.
 */
function evaluateRule(rule: SuccessRedirectRule, formData: Record<string, unknown>): boolean {
  // Evaluate single conditions (all must pass)
  if (rule.conditions && rule.conditions.length > 0) {
    if (!rule.conditions.every((c) => evaluateSingleCondition(c, formData))) {
      return false
    }
  }

  // Evaluate conditionsAll (all must pass)
  if (!evaluateAllConditions(rule.conditionsAll, formData)) {
    return false
  }

  // Evaluate conditionsAny (any group must pass)
  if (!evaluateAnyConditionGroup(rule.conditionsAny, formData)) {
    return false
  }

  return true
}

/**
 * Resolves which external success URL to redirect to based on form data and language.
 * Rules are evaluated in order; the first matching rule wins.
 * If no rule matches, returns null.
 *
 * @param rules - List of SuccessRedirectRule to evaluate
 * @param formData - Complete form data from all steps
 * @param language - Current language code (e.g., 'it', 'en', 'fr', 'es')
 * @returns The redirect URL if a rule matches, null otherwise
 */
export function resolveSuccessRedirectUrl(
  rules: SuccessRedirectRule[] | undefined,
  formData: Record<string, unknown>,
  language: string = 'it'
): string | null {
  if (!rules || rules.length === 0) {
    return null
  }

  for (const rule of rules) {
    if (evaluateRule(rule, formData)) {
      return resolveRedirectUrl(rule.redirectUrl, language)
    }
  }

  return null
}

/**
 * Resolves the fallback external success URL when no rules match.
 * Falls back to internal page if no fallbackUrl is configured.
 *
 * @param fallbackUrl - Either a string URL or a Record mapping language codes to URLs
 * @param language - Current language code (e.g., 'it', 'en', 'fr', 'es')
 * @returns The resolved fallback URL, or null to use internal success page
 */
export function resolveFallbackUrl(
  fallbackUrl: string | Record<string, string> | undefined,
  language: string = 'it'
): string | null {
  if (!fallbackUrl) {
    return null
  }

  return resolveRedirectUrl(fallbackUrl, language)
}
